import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Calendar, RefreshCw, AlertTriangle } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";

const CAT_COLOR = {
  FED:      "#ff8a4d",
  MACRO:    "#f5b042",
  EQUITIES: "#4ade80",
  CRYPTO:   "#4ad6ff",
  GEO:      "#ff5d6c",
};

function timeUntil(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return { label: "LIVE", urgent: true, expired: false, ms: 0 };
  const days = Math.floor(ms / 86400000);
  const hrs  = Math.floor((ms % 86400000) / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  if (days >= 1) return { label: `${days}d ${hrs}h`,  urgent: false, expired: false, ms };
  if (hrs >= 1)  return { label: `${hrs}h ${mins}m`,  urgent: true,  expired: false, ms };
  if (mins >= 1) return { label: `${mins}m`,           urgent: true,  expired: false, ms };
  return { label: "soon", urgent: true, expired: false, ms };
}

function fmtDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString([], { month: "short", day: "numeric" }) +
         " · " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function impactDots(impact) {
  return Array(3).fill(0).map((_, i) => i < impact);
}

export default function EconomicCalendar() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0); // force re-render for countdown

  const fetchEvents = useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}/api/calendar/events?limit=10`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && Array.isArray(d.events)) setEvents(d.events); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchEvents();
    const t = setInterval(fetchEvents, 30 * 60 * 1000); // refresh every 30 min
    return () => clearInterval(t);
  }, [fetchEvents]);

  // Tick every 60s to update countdown labels
  useEffect(() => {
    const t = setInterval(() => setTick(v => v + 1), 60000);
    return () => clearInterval(t);
  }, []);

  // Highest-impact event in next 24h
  const imminent = useMemo(() => {
    return events.find(e => {
      const tu = timeUntil(e.datetime);
      return e.impact === 3 && tu.ms < 24 * 3600 * 1000;
    });
  }, [events, tick]);

  return (
    <section className="hc-panel hub-section-calendar">
      <header className="hc-panel-hdr">
        <Calendar size={14} style={{ color: "#f5b042" }} />
        <span>Economic Calendar</span>
        <span className="hc-badge" style={{ color:"#f5b042", borderColor:"rgba(245,176,66,0.35)" }}>
          {events.length} upcoming
        </span>
        <button type="button" className="hc-news-refresh" onClick={fetchEvents} style={{ marginLeft: "auto" }}>
          <RefreshCw size={11} className={loading ? "hc-spin" : ""} />
        </button>
      </header>

      {/* Imminent alert ribbon */}
      {imminent && (
        <div className="cal-alert">
          <AlertTriangle size={11} />
          <span><strong>{imminent.name}</strong> in <strong>{timeUntil(imminent.datetime).label}</strong></span>
          <span className="cal-alert-blurb">{imminent.blurb}</span>
        </div>
      )}

      <div className="cal-list">
        {events.length === 0 ? (
          <div className="hc-trade-empty">
            <span className="hc-empty-icon"><Calendar size={16} /></span>
            <span>{loading ? "Loading calendar…" : "Calendar is clear"}</span>
            <small>{loading ? "Checking upcoming macro releases." : "No scheduled high-impact events in the current window."}</small>
          </div>
        ) : events.map((e, i) => {
          const tu = timeUntil(e.datetime);
          const color = CAT_COLOR[e.category] || "#8a93a3";
          const isHigh = e.impact === 3;
          return (
            <div key={i} className={`cal-row${isHigh ? " cal-row--high" : ""}`}
              style={{ "--cat-color": color }}>
              <div className="cal-row-left">
                <span className="cal-cat" style={{ background: `${color}18`, border: `1px solid ${color}44`, color }}>
                  {e.category}
                </span>
                <div className="cal-impact">
                  {impactDots(e.impact).map((on, k) => (
                    <span key={k} className="cal-impact-dot" style={{ background: on ? color : "rgba(255,255,255,0.1)" }} />
                  ))}
                </div>
              </div>

              <div className="cal-row-body">
                <p className="cal-name">{e.name}</p>
                <p className="cal-blurb">{e.blurb}</p>
              </div>

              <div className="cal-row-right">
                <span className={`cal-countdown${tu.urgent ? " cal-countdown--urgent" : ""}`} style={{ color: tu.urgent ? color : "rgba(255,255,255,0.55)" }}>
                  {tu.label}
                </span>
                <span className="cal-date">{fmtDate(e.datetime)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
